import { createRef, Dispatch, FC, KeyboardEvent, SetStateAction, useEffect } from 'react';
import { ORIENTATION_ENUMS, REQUIRED_ERROR } from 'constants/index';
import { Button } from 'destiny/dist/components/molecules/button';
import SvgSpriteLoader from 'destiny/dist/components/molecules/SvgSpriteLoader';
import Input from 'destiny/dist/components/organisms/input';
import { ICON_SPRITE_TYPES } from 'destiny/dist/constants/icons';
import { BUTTON_SIZE_TYPES, BUTTON_TYPES } from 'destiny/dist/constants/molecules/buttons';
import { SUPPORT_INFO_TYPES } from 'destiny/dist/constants/molecules/supporterInfo';
import { OptionsType } from 'destiny/dist/types/dropdown';
import { FormikProps } from 'formik';
import { SignUpInfoType } from 'modules/auth/auth.types';
import { trackMixpanel } from 'utils/mixpanel';
import CountryPicker from 'components/country-picker/CountryPicker';

interface SignUpDetailsFormPropsTypes {
  setSignUpInfo: Dispatch<SetStateAction<SignUpInfoType>>;
  signUpInfo: SignUpInfoType;
  signUpFormik: FormikProps<{
    email: string;
    firstName: string;
    lastName: string;
    token: string;
    merchantName: string;
    phone: string;
    phoneCode: string;
    countryCode: string;
    referralCode: string;
  }>;
}

const SignUpDetailsForm: FC<SignUpDetailsFormPropsTypes> = ({ signUpInfo, setSignUpInfo, signUpFormik }) => {
  const nameInputRef = createRef<HTMLInputElement>();

  const errorIconAttributes = {
    id: 'alert-triangle',
    iconCategory: ICON_SPRITE_TYPES.ALERTS_AND_FEEDBACK,
    width: 12,
    height: 12,
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      signUpFormik.submitForm();
    }
  };

  useEffect(() => {
    nameInputRef?.current?.focus();
  }, [nameInputRef?.current]);

  return (
    <div className='tw-mt-9'>
      <div className='tw-mt-8 tw-flex tw-gap-4 tw-mb-2'>
        <Input
          inputFieldProps={{
            inputTagProps: {
              placeHolder: '',
              name: 'firstName',
              error: signUpFormik.touched.firstName ? (signUpFormik.errors.firstName as string) : '',
              onBlur: signUpFormik.handleBlur,
              value: signUpFormik.values.firstName,
              onChange: signUpFormik.handleChange,
              onKeyPress: handleKeyPress,
              inputRef: nameInputRef,
              id: 'SIGNUP_FIRST_NAME_INPUT',
            },
            size: 'SMALL',
          }}
          labelProps={{
            title: 'First Name',
          }}
          showLabel
          showSupporterInfo={!!signUpFormik.touched.firstName && !!signUpFormik.errors.firstName}
          supporterInfoProps={{
            text: signUpFormik.touched.firstName ? (signUpFormik.errors.firstName as string) : '',
            type: SUPPORT_INFO_TYPES.ERROR,
          }}
        />
        <Input
          inputFieldProps={{
            inputTagProps: {
              placeHolder: '',
              name: 'lastName',
              onBlur: signUpFormik.handleBlur,
              value: signUpFormik.values.lastName,
              onChange: signUpFormik.handleChange,
              onKeyPress: handleKeyPress,
              error: signUpFormik.touched.lastName ? (signUpFormik.errors.lastName as string) : '',
              id: 'SIGNUP_LAST_NAME_INPUT',
            },
            size: 'SMALL',
          }}
          labelProps={{
            title: 'Last Name',
          }}
          showLabel
          showSupporterInfo={!!signUpFormik.touched.lastName && !!signUpFormik.errors.lastName}
          supporterInfoProps={{
            text: signUpFormik.touched.lastName ? (signUpFormik.errors.lastName as string) : '',
            type: SUPPORT_INFO_TYPES.ERROR,
          }}
        />
      </div>
      <Input
        wrapperClass='tw-pb-2'
        inputFieldProps={{
          inputTagProps: {
            placeHolder: '',
            name: 'email',
            onBlur: signUpFormik.handleBlur,
            value: signUpFormik.values.email,
            onChange: signUpFormik.handleChange,
            onKeyPress: handleKeyPress,
            error: signUpFormik.touched.email ? (signUpFormik.errors.email as string) : '',
            id: 'SIGNUP_EMAIL_INPUT',
          },
          size: 'SMALL',
        }}
        showSupporterInfo={!!signUpFormik.touched.email && !!signUpFormik.errors.email}
        supporterInfoProps={{
          text: signUpFormik.touched.email ? (signUpFormik.errors.email as string) : '',
          type: SUPPORT_INFO_TYPES.ERROR,
          iconAttributes: signUpFormik.errors.email !== REQUIRED_ERROR ? errorIconAttributes : undefined,
        }}
        labelProps={{
          title: 'Business Email',
        }}
        showLabel
      />
      <div className='tw-flex tw-gap-4 tw-mb-2'>
        <CountryPicker
          label='Phone No.'
          id='phone'
          labelClassName='f-14-500 tw-mb-1 tw-text-TEXT_PRIMARY'
          selectedValueKey='dial_code'
          onMenuItemClick={(phoneRegion: OptionsType[]) => {
            const value = phoneRegion[0];

            signUpFormik.setFieldValue('phoneCode', value.value);
            setSignUpInfo({ ...signUpInfo, phoneRegion: value });
          }}
          defaultValue={signUpInfo.phoneRegion}
          onBlur={signUpFormik.handleBlur}
          error={signUpFormik.touched.phoneCode ? (signUpFormik.errors.phoneCode as string) : ''}
          orientation={ORIENTATION_ENUMS.HORIZONTAL}
          customControlStyles={{
            padding: '0px 24px',
            minHeight: '44px',
            height: '44px',
          }}
        />
        <div className='tw-grow tw-mt-[18px]'>
          <Input
            inputFieldProps={{
              inputTagProps: {
                placeHolder: 'Phone number',
                name: 'phone',
                onBlur: signUpFormik.handleBlur,
                value: signUpFormik.values.phone,

                onChange: signUpFormik.handleChange,
                onKeyPress: handleKeyPress,
                error: signUpFormik.touched.phone ? (signUpFormik.errors.phone as string) : '',
                id: 'SIGNUP_PHONE_INPUT',
              },
              size: 'SMALL',
            }}
            showSupporterInfo={!!signUpFormik.touched.phone && !!signUpFormik.errors.phone}
            supporterInfoProps={{
              text: signUpFormik.touched.phone ? (signUpFormik.errors.phone as string) : '',
              type: SUPPORT_INFO_TYPES.ERROR,
            }}
          />
        </div>
      </div>
      <Input
        wrapperClass='tw-pb-2'
        inputFieldProps={{
          inputTagProps: {
            placeHolder: '',
            name: 'referralCode',
            onBlur: signUpFormik.handleBlur,
            value: signUpFormik.values.referralCode,
            onChange: signUpFormik.handleChange,
            onKeyPress: handleKeyPress,
            error: signUpFormik.touched.referralCode ? (signUpFormik.errors.referralCode as string) : '',
            id: 'SIGNUP_REFERRAL_INPUT',
          },
          size: 'SMALL',
        }}
        showSupporterInfo={!!signUpFormik.touched.referralCode && !!signUpFormik.errors.referralCode}
        supporterInfoProps={{
          text: signUpFormik.touched.referralCode ? (signUpFormik.errors.referralCode as string) : '',
          type: SUPPORT_INFO_TYPES.ERROR,
        }}
        labelProps={{
          title: 'Referral Code (Optional)',
        }}
        showLabel
      />
      <Button
        showTrailingIcon={true}
        customTrailingIcon={
          <SvgSpriteLoader id='arrow-right' iconCategory={ICON_SPRITE_TYPES.ARROWS} width={16} height={16} />
        }
        buttonProps={{
          size: BUTTON_SIZE_TYPES.MEDIUM,
          btnType: BUTTON_TYPES.PRIMARY,
          wrapperClass: 'tw-mt-5 tw-w-[114px] tw-overflow-clip tw-outline-none',
          onClick: signUpFormik.submitForm,
          disabled: !signUpFormik.isValid || !signUpFormik.dirty,
          id: 'SIGNUP_CREATE_ACCOUNT_BUTTON',
          eventCallback: (id, data) => {
            trackMixpanel(id, { ...data, error: signUpFormik.errors });
          },
        }}
      >
        Next
      </Button>
    </div>
  );
};

export default SignUpDetailsForm;
